import { createElement as rc, useCallback, useContext, useEffect, useRef } from 'react';
import { ToastContainer, toast, Slide } from 'react-toastify';
import log from '@sstdev/lib_logging';
import UiNotificationContext from '../contexts/UiNotificationContext';
import { uiRuntimeConfig, constants } from 'lib_ui-services';
import styled from '../styled';
import fromTheme from '../fromTheme';
import cuid from 'cuid';
import { useTheme } from 'styled-components';
import defaultIcons from '../components/Icons/PredefinedFeedbackIcon';
import webOnlyStyles from '../webOnlyStyles';
const { InfoFeedbackIcon, ErrorFeedbackIcon, SuccessFeedbackIcon, WarningFeedbackIcon } = defaultIcons;

/*
Ideally, we'd use the following styling, but somehow it does not seem to work:
 .Toastify__toast-container--bottom-left {
        bottom: ${fromTheme('toast', 'margin')};
        left: ${fromTheme('toast', 'margin')};
    }
*/

let StyledToast = styled(ToastContainer).attrs({ name: 'toast-container' })`
    .Toastify__toast {
        padding: ${fromTheme('toast', 'padding')};
        align-items: start;
        ${fromTheme('toast', 'font')}
        border-radius: ${fromTheme('toast', 'radius')};

        ${({ theme }) => {
            if (theme?.mobile) {
                return `margin-top: ${theme.toast?.extraMargin}px;
                        flex-direction: column;`;
            } else {
                return `margin-bottom: ${theme.toast?.extraMargin}px;
                    margin-left: ${theme.toast?.extraMargin}px;
                    flex-direction: row;`;
            }
        }}
    }

    .Toastify__toast-icon {
        margin-inline-end: 0;
        width: ${fromTheme('toast', 'iconSpace', 'width')};
        height: ${fromTheme('toast', 'iconSpace', 'height')};
    }

    .Toastify__close-button > svg {
        width: ${fromTheme('toast', 'buttonSize')};
        height: ${fromTheme('toast', 'buttonSize')};
    }
    .Toastify__close-button {
        color: ${fromTheme('toast', 'color')};
        opacity: 1;
    }

    .Toastify__progress-bar {
        background: ${fromTheme('toast', 'progressBarColor')};
    }
    .Toastify__progress-bar--wrp {
        border-bottom-left-radius: ${fromTheme('toast', 'radius')};
        border-bottom-right-radius: ${fromTheme('toast', 'radius')};
    }

    .Toastify__toast--success {
        background: ${fromTheme('toast', 'success', 'backgroundColor')};
        color: ${fromTheme('toast', 'color')} !important;
    }
    .Toastify__toast--error {
        background: ${fromTheme('toast', 'error', 'backgroundColor')};
        color: ${fromTheme('toast', 'color')} !important;
    }
    .Toastify__toast--warning {
        background: ${fromTheme('toast', 'warning', 'backgroundColor')};
        color: ${fromTheme('toast', 'color')} !important;
    }
    .Toastify__toast--info {
        background: ${fromTheme('toast', 'info', 'backgroundColor')};
        color: ${fromTheme('toast', 'color')} !important;
    }
    .Toastify__toast--default {
        background: ${fromTheme('toast', 'info', 'backgroundColor')};
        color: ${fromTheme('toast', 'color')} !important;
    }
`;
StyledToast = webOnlyStyles(StyledToast)`
    .Toastify__toast {
        box-shadow: ${fromTheme('toast', 'boxShadow')};
    }
`;

let currentMessage = '';
export default function Toast() {
    const theme = useTheme();
    const notificationContext = useContext(UiNotificationContext);
    const containerId = useRef(cuid());

    // Most of the time, toast messages come in through the typeHandler
    useEffect(() => {
        const removeHandler = notificationContext.addTypeHandler(constants.notificationTypes.TOAST, payload => {
            const { message, timeout = uiRuntimeConfig.getNotificationTimeout(), isError, variant: _variant } = payload;
            if (!_variant) {
                log.warn(
                    `DEPRECATION WARNING: Toast notifications should use 'variant' ["error"|"warning"|"success"|"info"]  instead of "isError" to indicate the type of toast. Please update your code: ${JSON.stringify(payload)}`
                );
            }
            const variant = _variant || (isError ? 'error' : 'info');

            // Prevent displaying the same message twice:
            if (currentMessage && currentMessage === message) return;
            currentMessage = message;

            // Keep track of which toasts are open
            const id = cuid();
            notificationContext.openToasts.push({
                id,
                message,
                timeout,
                isError
            });

            const onClose = () => {
                // We do want Errors to be rethrown (though not shown multiple times at the same time).
                // So, reset the "currentMessage" AFTER an error popup has closed
                currentMessage = '';

                // Remove this toast from the context when it closes.
                const toasts = notificationContext.openToasts;
                const thisIndex = toasts.findIndex(t => t.id === id);
                if (thisIndex >= 0) {
                    toasts.splice(thisIndex, 1);
                }
            };
            if (toast[variant]) {
                toast[variant](message, {
                    containerId: containerId.current,
                    autoClose: timeout,
                    onClose
                });
            } else {
                toast(message, {
                    containerId: containerId.current,
                    autoClose: timeout,
                    onClose
                });
            }
        });
        return removeHandler;
    }, [notificationContext]);

    // Sometimes a new instance of this component will be rendered (in a modal for instance)
    // and take precedence.  If there are any toasts already supposed to be displayed, then this new instance
    // should display them as well.
    useEffect(() => {
        const toastsInThisInstance = [];
        notificationContext.openToasts.forEach(t => {
            // open toasts will already have gone through the typeHandler, so we don't need to warn about missing variant here.
            const { message, timeout, isError, variant = isError ? 'error' : 'info' } = t;
            const correlationId = cuid();
            const onClose = () => {
                const thisIndex = toastsInThisInstance.findIndex(t => t.correlationId === correlationId);
                toastsInThisInstance.splice(thisIndex, 1);
            };
            if (toast[variant]) {
                toastsInThisInstance.push({
                    correlationId,
                    internalId: toast[variant](message, {
                        containerId: containerId.current,
                        onClose,
                        autoClose: timeout || constants.TOAST_TIMEOUT
                    })
                });
            } else {
                toastsInThisInstance.push({
                    correlationId,
                    internalId: toast(message, {
                        containerId: containerId.current,
                        onClose,
                        autoClose: timeout || constants.TOAST_TIMEOUT
                    })
                });
            }
        });
        return () => {
            toastsInThisInstance.forEach(t => {
                toast.dismiss(t.internalId);
            });
        };
    }, [notificationContext.openToasts]);

    // react-toastify calls it "type", but we already use that for the notification type (e.g. toast vs beep)
    const icon = useCallback(({ type: variant }) => {
        switch (variant) {
            case 'error':
                return rc(ErrorFeedbackIcon, { variant: 'secondary', size: 'sm' });
            case 'warning':
                return rc(WarningFeedbackIcon, { variant: 'secondary', size: 'sm' });
            case 'success':
                return rc(SuccessFeedbackIcon, { variant: 'secondary', size: 'sm' });
            case 'info':
            default:
                return rc(InfoFeedbackIcon, { variant: 'secondary', size: 'sm' });
        }
    }, []);

    return rc(StyledToast, {
        icon,
        stacked: false,
        enableMultiContainer: true,
        position: theme.mobile ? 'top-center' : 'bottom-left',
        autoClose: uiRuntimeConfig.getNotificationTimeout(),
        newestOnTop: true,
        closeOnClick: true,
        draggable: true,
        pauseOnHover: true,
        pauseOnFocusLoss: true,
        containerId: containerId.current,
        transition: Slide
    });
}
